import { useEffect, useRef, useState } from "react";

const YOUTUBE_THUMBNAIL_MIN_WIDTH = 120;
const YOUTUBE_EMBED_READY_MAX_MS = 60000;
const BACKOFF_DELAYS_MS = [2000, 4000, 8000];

const getYoutubeThumbnailUrl = ({ youtubeVideoId }) =>
  `https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg`;

const getBackoffDelayMs = ({ attemptIndex }) => {
  const index = Math.min(attemptIndex, BACKOFF_DELAYS_MS.length - 1);
  return BACKOFF_DELAYS_MS[index];
};

const loadYoutubeThumbnail = ({ youtubeVideoId }) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        naturalWidth: img.naturalWidth,
        hasError: false,
      });
    };
    img.onerror = () => {
      resolve({
        naturalWidth: 0,
        hasError: true,
      });
    };
    img.src = getYoutubeThumbnailUrl({ youtubeVideoId });
  });

export const useYoutubeEmbedReady = ({ youtubeVideoId }) => {
  const [isEmbedReady, setIsEmbedReady] = useState(false);
  const timeoutRef = useRef(null);
  const startedAtRef = useRef(0);
  const attemptRef = useRef(0);

  useEffect(() => {
    if (!youtubeVideoId) {
      setIsEmbedReady(false);
      return undefined;
    }

    let cancelled = false;
    startedAtRef.current = Date.now();
    attemptRef.current = 0;
    setIsEmbedReady(false);

    const clearScheduledCheck = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const markReady = () => {
      if (cancelled) {
        return;
      }

      clearScheduledCheck();
      setIsEmbedReady(true);
    };

    const scheduleNextCheck = () => {
      clearScheduledCheck();
      const delayMs = getBackoffDelayMs({ attemptIndex: attemptRef.current });
      timeoutRef.current = setTimeout(() => {
        void runCheck();
      }, delayMs);
    };

    const runCheck = async () => {
      if (cancelled) {
        return;
      }

      const elapsedMs = Date.now() - startedAtRef.current;
      if (elapsedMs >= YOUTUBE_EMBED_READY_MAX_MS) {
        markReady();
        return;
      }

      const { naturalWidth } = await loadYoutubeThumbnail({ youtubeVideoId });

      if (cancelled) {
        return;
      }

      if (naturalWidth > YOUTUBE_THUMBNAIL_MIN_WIDTH) {
        markReady();
        return;
      }

      attemptRef.current += 1;

      const elapsedAfterCheckMs = Date.now() - startedAtRef.current;
      if (elapsedAfterCheckMs >= YOUTUBE_EMBED_READY_MAX_MS) {
        markReady();
        return;
      }

      scheduleNextCheck();
    };

    void runCheck();

    return () => {
      cancelled = true;
      clearScheduledCheck();
    };
  }, [youtubeVideoId]);

  return { isEmbedReady };
};
